import './style.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createIncidenceAction,
  getIncidenceAction,
  getIncidenceActionContext,
  getIncidenceActionGroupParticipants,
  removeIncidenceActionSignature,
  saveIncidenceActionSignature,
  updateIncidenceAction
} from '../../API/apiService';
import { constants } from '../../config/constants';
import CustomFilterSelect from '../../shared/customFilterSelect';
import { getMultiFilterLabel } from '../../utils/multiFilter';
import ModalContainer from '../../components/modalContainer';
import SignatureCanvas from '../../components/signatureCanvas';
import { downloadIncidenceAttendance093 } from '../../utils/incidenceActionAttendance';
import EvidenceReviewStatus from '../../components/evidenceReviewStatus';

const INITIAL_FORM = {
  name: '',
  mediatorIds: [],
  groupIds: [],
  actionDate: '',
  startTime: '',
  endTime: '',
  topic: '',
  municipality: '',
  neighborhood: '',
  place: '',
  address: '',
  logbook: null,
  methodology: null,
  attendance: null
};

function splitStoredList(value) {
  return String(value || '').split(',').map(item => item.trim()).filter(Boolean);
}

function formatAttendanceClose(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Bogota'
  }).format(date);
}

function IncidenceActionForm() {
  const navigate = useNavigate();
  const { actionId } = useParams();
  const isEditing = Boolean(actionId);
  const currentUserId = localStorage.getItem(constants.USER_ID);
  const [context, setContext] = useState({
    mediators: [],
    groups: [],
    municipalities: [],
    neighborhoods: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingAttendance, setDownloadingAttendance] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [review, setReview] = useState({ status: 'pending', observations: '' });
  const [signatureParticipant, setSignatureParticipant] = useState(null);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const [groupsDirty, setGroupsDirty] = useState(false);
  const [attendanceWindow, setAttendanceWindow] = useState({
    attendanceStartedAt: null,
    attendanceClosesAt: null
  });
  const [clock, setClock] = useState(Date.now());
  const participantRequest = useRef(0);
  const signatureRef = useRef(null);

  useEffect(() => {
    let active = true;

    Promise.all([
      getIncidenceActionContext(),
      isEditing ? getIncidenceAction(actionId) : Promise.resolve(null)
    ]).then(([data, savedAction]) => {
      if (!active) return;
      const mediators = Array.isArray(data?.mediators) ? data.mediators : [];
      setContext({
        mediators,
        groups: Array.isArray(data?.groups) ? data.groups : [],
        municipalities: Array.isArray(data?.municipalities) ? data.municipalities : [],
        neighborhoods: Array.isArray(data?.neighborhoods) ? data.neighborhoods : []
      });

      if (isEditing && !savedAction) {
        navigate('/acciones-incidencia');
        return;
      }

      if (savedAction) {
        const savedMediatorNames = new Set(
          splitStoredList(savedAction.mediators).map(name => name.toLocaleLowerCase('es'))
        );
        setForm({
          ...INITIAL_FORM,
          name: savedAction.name || '',
          mediatorIds: mediators
            .filter(item => savedMediatorNames.has(String(item.name || '').trim().toLocaleLowerCase('es')))
            .map(item => String(item.id)),
          groupIds: Array.isArray(savedAction.groups)
            ? savedAction.groups.map(group => String(group.id))
            : [],
          actionDate: savedAction.actionDate || '',
          startTime: String(savedAction.startTime || '').slice(0, 5),
          endTime: String(savedAction.endTime || '').slice(0, 5),
          topic: savedAction.topic || '',
          municipality: savedAction.municipality || '',
          neighborhood: savedAction.neighborhood || '',
          place: savedAction.place || '',
          address: savedAction.address || ''
        });
        const savedParticipants = Array.isArray(savedAction.participants)
          ? savedAction.participants
          : [];
        const savedGroupIds = Array.isArray(savedAction.groups)
          ? savedAction.groups.map(group => String(group.id))
          : [];
        setParticipants(savedParticipants);
        if (savedGroupIds.length > 0) {
          setLoadingParticipants(true);
          getIncidenceActionGroupParticipants(savedGroupIds).then(roster => {
            if (!active) return;
            setLoadingParticipants(false);
            if (!roster) return;
            const savedById = new Map(
              savedParticipants.map(participant => [String(participant.id), participant])
            );
            setParticipants(roster.map(participant => {
              const previous = savedById.get(String(participant.id));
              return {
                ...participant,
                attended: previous?.hasSignature === true,
                hasSignature: previous?.hasSignature === true,
                signature: null
              };
            }));
          });
        }
        setExistingEvidences(Array.isArray(savedAction.evidences) ? savedAction.evidences : []);
        setReview({
          status: savedAction.reviewStatus || 'pending',
          observations: savedAction.reviewObservations || ''
        });
        setAttendanceWindow({
          attendanceStartedAt: savedAction.attendanceStartedAt || null,
          attendanceClosesAt: savedAction.attendanceClosesAt || null
        });
      } else {
        const currentMediator = mediators.find(item => String(item.id) === String(currentUserId));
        if (currentMediator) {
          setForm(current => ({ ...current, mediatorIds: [String(currentMediator.id)] }));
        }
      }
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [actionId, currentUserId, isEditing, navigate]);

  useEffect(() => {
    if (!attendanceWindow.attendanceClosesAt) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [attendanceWindow.attendanceClosesAt]);

  const attendanceClosed = Boolean(
    attendanceWindow.attendanceClosesAt &&
    clock >= new Date(attendanceWindow.attendanceClosesAt).getTime()
  );

  const mediatorOptions = useMemo(() => context.mediators.map(mediator => ({
    id: mediator.id,
    name: mediator.name
  })), [context.mediators]);

  const selectedMediatorRecords = useMemo(() => {
    const selectedIds = new Set(form.mediatorIds.map(String));
    return context.mediators
      .filter(item => selectedIds.has(String(item.id)));
  }, [context.mediators, form.mediatorIds]);

  const selectedMediators = useMemo(
    () => selectedMediatorRecords.map(item => item.name),
    [selectedMediatorRecords]
  );

  const filteredNeighborhoods = useMemo(() => {
    const municipality = context.municipalities.find(item => item.name === form.municipality);
    if (!municipality) return [];
    return context.neighborhoods.filter(item => (
      String(item.municipalityId) === String(municipality.id)
    ));
  }, [context.municipalities, context.neighborhoods, form.municipality]);

  function updateForm(name, value) {
    setForm(current => ({
      ...current,
      [name]: value,
      ...(name === 'municipality' ? { neighborhood: '' } : {})
    }));
  }

  async function loadGroupParticipants(groupIds) {
    const requestId = participantRequest.current + 1;
    participantRequest.current = requestId;
    if (groupIds.length === 0) {
      setParticipants([]);
      setLoadingParticipants(false);
      return;
    }

    setLoadingParticipants(true);
    const roster = await getIncidenceActionGroupParticipants(groupIds);
    if (requestId !== participantRequest.current) return;
    setLoadingParticipants(false);
    if (!roster) return;
    setParticipants(current => {
      const participantById = new Map(
        current.map(participant => [String(participant.id), participant])
      );
      return roster.map(participant => {
        const previous = participantById.get(String(participant.id));
        return {
          ...participant,
          attended: previous?.hasSignature === true,
          hasSignature: previous?.hasSignature === true,
          signature: previous?.signature || null
        };
      });
    });
  }

  function selectMediators(values) {
    const mediatorIds = Array.isArray(values) ? values : [];
    const selectedMediatorIds = new Set(mediatorIds.map(String));
    const allowedGroupIds = new Set(context.groups
      .filter(group => selectedMediatorIds.has(String(group.mediatorId)))
      .map(group => String(group.id)));
    const groupIds = form.groupIds.filter(groupId => allowedGroupIds.has(String(groupId)));
    setForm(current => ({ ...current, mediatorIds, groupIds }));
    setGroupsDirty(true);
    if (groupIds.length !== form.groupIds.length) loadGroupParticipants(groupIds);
  }

  function selectMediatorGroups(mediatorId, values) {
    const mediatorGroupIds = new Set(context.groups
      .filter(group => String(group.mediatorId) === String(mediatorId))
      .map(group => String(group.id)));
    const groupIds = [
      ...form.groupIds.filter(groupId => !mediatorGroupIds.has(String(groupId))),
      ...(Array.isArray(values) ? values : [])
    ];
    setForm(current => ({ ...current, groupIds }));
    setGroupsDirty(true);
    loadGroupParticipants(groupIds);
  }

  function openSignature(participant) {
    if (!isEditing) {
      toast('Guarda primero la acción de incidencia para registrar firmas', { type: 'info' });
      return;
    }
    if (groupsDirty) {
      toast('Guarda los cambios de mediadores y grupos antes de registrar firmas', { type: 'info' });
      return;
    }
    if (attendanceClosed) {
      toast('El periodo de asistencia ya finalizó', { type: 'warning' });
      return;
    }
    setSignatureParticipant(participant);
    setHasDrawnSignature(false);
  }

  function closeSignature() {
    if (savingSignature) return;
    setSignatureParticipant(null);
    setHasDrawnSignature(false);
  }

  async function confirmSignature() {
    if (!signatureRef.current?.hasDrawn()) {
      toast('La persona debe firmar antes de confirmar su asistencia', { type: 'warning' });
      return;
    }
    const signature = signatureRef.current.toDataURL();
    if (!signature) {
      toast('No se pudo capturar la firma. Intenta nuevamente', { type: 'error' });
      return;
    }
    setSavingSignature(true);
    const result = await saveIncidenceActionSignature(
      actionId,
      signatureParticipant.id,
      signature
    );
    setSavingSignature(false);
    if (!result.success) {
      if (result.code === 'INCIDENCE_ACTION_ATTENDANCE_CLOSED') {
        setClock(Date.now());
        closeSignature();
      }
      return;
    }
    setParticipants(current => current.map(participant => (
      String(participant.id) === String(signatureParticipant.id)
        ? { ...participant, attended: true, hasSignature: true, signature: null }
        : participant
    )));
    setAttendanceWindow({
      attendanceStartedAt: result.data?.attendanceStartedAt || null,
      attendanceClosesAt: result.data?.attendanceClosesAt || null
    });
    setClock(Date.now());
    closeSignature();
  }

  async function removeSignature() {
    setSavingSignature(true);
    const result = await removeIncidenceActionSignature(actionId, signatureParticipant.id);
    setSavingSignature(false);
    if (!result.success) {
      if (result.code === 'INCIDENCE_ACTION_ATTENDANCE_CLOSED') {
        setClock(Date.now());
        closeSignature();
      }
      return;
    }
    setParticipants(current => current.map(participant => (
      String(participant.id) === String(signatureParticipant.id)
        ? { ...participant, attended: false, hasSignature: false, signature: null }
        : participant
    )));
    setAttendanceWindow({
      attendanceStartedAt: result.data?.attendanceStartedAt || null,
      attendanceClosesAt: result.data?.attendanceClosesAt || null
    });
    closeSignature();
  }

  async function downloadAttendance() {
    setDownloadingAttendance(true);
    try {
      await downloadIncidenceAttendance093({ id: actionId, name: form.name });
      toast('Descarga de asistencia iniciada', { type: 'success' });
    } catch (error) {
      toast('No fue posible descargar la asistencia', { type: 'error' });
    } finally {
      setDownloadingAttendance(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (saving) return;
    if (loadingParticipants) {
      toast('Espera mientras se cargan los participantes de los grupos', { type: 'info' });
      return;
    }

    if (selectedMediators.length === 0) {
      toast('Selecciona al menos un mediador', { type: 'warning' });
      return;
    }
    if (form.groupIds.length === 0) {
      toast('Selecciona al menos un grupo', { type: 'warning' });
      return;
    }
    if (form.endTime <= form.startTime) {
      toast('La hora final debe ser posterior a la hora de inicio', { type: 'warning' });
      return;
    }
    if (attendanceClosed) {
      const hasLogbook = Boolean(form.logbook) || existingEvidences.some(
        evidence => evidence.name === 'bitacora'
      );
      const hasMethodology = Boolean(form.methodology) || existingEvidences.some(
        evidence => evidence.name === 'ficha_metodologica'
      );
      if (!hasLogbook || !hasMethodology) {
        toast('Al finalizar la asistencia debes cargar la bitácora y la ficha metodológica', {
          type: 'warning'
        });
        return;
      }
    }

    setSaving(true);
    const payload = { ...form };
    const result = isEditing
      ? await updateIncidenceAction(actionId, payload)
      : await createIncidenceAction(payload);
    setSaving(false);
    if (!result.success) return;

    toast(
      isEditing
        ? 'Acción de incidencia actualizada exitosamente'
        : 'Acción de incidencia guardada exitosamente',
      { type: 'success' }
    );
    setGroupsDirty(false);
    if (!isEditing && result.data?.id) {
      navigate(`/acciones-incidencia/${result.data.id}`);
      return;
    }
    navigate('/acciones-incidencia');
  }

  return (
    <section className='incidence-action-page'>
      {signatureParticipant && (
        <ModalContainer>
          <div
            className='incidence-signature-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='incidence-signature-title'>
            <button
              type='button'
              className='incidence-signature-modal__close'
              disabled={savingSignature}
              onClick={closeSignature}
              aria-label='Cerrar firma'>
              <span className='material-symbols-rounded' aria-hidden='true'>close</span>
            </button>
            <h2 id='incidence-signature-title'>Firma de asistencia</h2>
            <p className='incidence-signature-modal__name'>{signatureParticipant.fullName}</p>
            <p className='incidence-signature-modal__description'>
              La asistencia se confirma únicamente cuando la persona firma en el recuadro.
            </p>
            <SignatureCanvas
              ref={signatureRef}
              height={210}
              onDrawChange={setHasDrawnSignature}
            />
            <div className='incidence-signature-modal__actions'>
              <button type='button' className='cancel' disabled={savingSignature} onClick={closeSignature}>Cancelar</button>
              {signatureParticipant.hasSignature && (
                <button type='button' className='remove' disabled={savingSignature} onClick={removeSignature}>
                  Marcar que no asistió
                </button>
              )}
              <button
                type='button'
                className='confirm'
                disabled={!hasDrawnSignature || savingSignature}
                onClick={confirmSignature}>
                {savingSignature ? 'Guardando firma...' : 'Confirmar firma y asistencia'}
              </button>
            </div>
          </div>
        </ModalContainer>
      )}
      <div className='incidence-action-card'>
        <header className='incidence-action-header'>
          <div>
            <p className='incidence-action-eyebrow'>EVIDENCIAS</p>
            <h1>{isEditing ? 'Visualizar y editar acción' : 'Cargar acción de incidencia'}</h1>
            <p>
              {isEditing
                ? 'Consulta la información registrada y realiza los ajustes necesarios.'
                : 'Registra la actividad, sus participantes y los soportes correspondientes.'}
            </p>
          </div>
          {isEditing && (
            <EvidenceReviewStatus status={review.status} observations={review.observations} />
          )}
        </header>

        {loading ? (
          <p className='incidence-action-state'>Cargando formulario...</p>
        ) : (
          <form onSubmit={submit} className='incidence-action-form'>
            <section className='incidence-action-section'>
              <h2>Información de la acción</h2>
              <label className='incidence-action-field incidence-action-field--wide'>
                <span>Nombre de la acción de incidencia *</span>
                <input
                  required
                  maxLength='255'
                  value={form.name}
                  onChange={event => updateForm('name', event.target.value)}
                />
              </label>

              <div className='incidence-action-grid incidence-action-grid--three'>
                <label className='incidence-action-field'>
                  <span>Fecha *</span>
                  <input
                    required
                    type='date'
                    value={form.actionDate}
                    onChange={event => updateForm('actionDate', event.target.value)}
                  />
                </label>
                <label className='incidence-action-field'>
                  <span>Hora de inicio *</span>
                  <input
                    required
                    type='time'
                    value={form.startTime}
                    onChange={event => updateForm('startTime', event.target.value)}
                  />
                </label>
                <label className='incidence-action-field'>
                  <span>Hora final *</span>
                  <input
                    required
                    type='time'
                    value={form.endTime}
                    onChange={event => updateForm('endTime', event.target.value)}
                  />
                </label>
              </div>

              <label className='incidence-action-field incidence-action-field--wide'>
                <span>Tema *</span>
                <input
                  required
                  maxLength='500'
                  value={form.topic}
                  onChange={event => updateForm('topic', event.target.value)}
                />
              </label>
              <div className='incidence-action-grid'>
                <label className='incidence-action-field'>
                  <span>Comuna *</span>
                  <select
                    required
                    value={form.municipality}
                    onChange={event => updateForm('municipality', event.target.value)}>
                    <option value=''>Seleccione</option>
                    {context.municipalities.map(municipality => (
                      <option key={municipality.id} value={municipality.name}>
                        {municipality.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className='incidence-action-field'>
                  <span>Barrio *</span>
                  <select
                    required
                    disabled={!form.municipality}
                    value={form.neighborhood}
                    onChange={event => updateForm('neighborhood', event.target.value)}>
                    <option value=''>Seleccione</option>
                    {filteredNeighborhoods.map(neighborhood => (
                      <option key={neighborhood.id} value={neighborhood.name}>
                        {neighborhood.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className='incidence-action-grid'>
                <label className='incidence-action-field'>
                  <span>Lugar *</span>
                  <input
                    required
                    maxLength='255'
                    value={form.place}
                    onChange={event => updateForm('place', event.target.value)}
                  />
                </label>
                <label className='incidence-action-field'>
                  <span>Dirección *</span>
                  <input
                    required
                    maxLength='255'
                    value={form.address}
                    onChange={event => updateForm('address', event.target.value)}
                  />
                </label>
              </div>
            </section>

            <section className='incidence-action-section'>
              <h2>Mediadores y grupos</h2>
              <div className='incidence-action-field incidence-action-field--wide'>
                <span>Mediadores participantes *</span>
                <CustomFilterSelect
                  optionsList={mediatorOptions}
                  placeholder='Selecciona uno o varios mediadores'
                  valueEdit={getMultiFilterLabel(mediatorOptions, form.mediatorIds)}
                  addInfo={(_, values) => selectMediators(values)}
                  multiple
                  selectedValues={form.mediatorIds}
                  searchable
                  searchPlaceholder='Buscar mediador'
                />
              </div>

              {selectedMediatorRecords.length === 0 ? (
                <p className='incidence-action-empty'>Selecciona primero los mediadores participantes.</p>
              ) : (
                <div className='incidence-group-selectors'>
                  {selectedMediatorRecords.map(mediator => {
                    const options = context.groups
                      .filter(group => String(group.mediatorId) === String(mediator.id))
                      .map(group => ({
                        id: group.id,
                        name: `${group.code}${group.schoolName ? ` · ${group.schoolName}` : ''}`
                      }));
                    const selectedValues = form.groupIds.filter(groupId => (
                      options.some(option => String(option.id) === String(groupId))
                    ));
                    return (
                      <div className='incidence-group-selector' key={mediator.id}>
                        <strong>{mediator.name}</strong>
                        {options.length === 0 ? (
                          <small>Este mediador no tiene grupos registrados.</small>
                        ) : (
                          <div className='incidence-action-field'>
                            <span>Grupos participantes *</span>
                            <CustomFilterSelect
                              optionsList={options}
                              placeholder='Selecciona uno o varios grupos'
                              valueEdit={getMultiFilterLabel(options, selectedValues)}
                              addInfo={(_, values) => selectMediatorGroups(mediator.id, values)}
                              multiple
                              selectedValues={selectedValues}
                              searchable
                              searchPlaceholder='Buscar grupo'
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className='incidence-action-section'>
              <h2>Participantes y asistencia</h2>
              <div className={`incidence-attendance-window${
                attendanceClosed ? ' incidence-attendance-window--closed' : ''
              }`}>
                {!isEditing
                  ? 'Guarda primero la acción. Después podrás registrar las firmas.'
                  : groupsDirty
                    ? 'Guarda los cambios de mediadores y grupos antes de continuar con las firmas.'
                    : attendanceClosed
                      ? 'El periodo de asistencia finalizó. Ya no se pueden agregar ni retirar firmas.'
                      : attendanceWindow.attendanceClosesAt
                        ? `La asistencia estará disponible hasta el ${formatAttendanceClose(attendanceWindow.attendanceClosesAt)}.`
                        : 'El periodo de 2 horas comenzará cuando se registre la primera firma.'}
              </div>
              {participants.length > 0 && (
                <p className='incidence-attendance-summary'>
                  {participants.filter(participant => participant.hasSignature).length} de{' '}
                  {participants.length} participantes han firmado.
                </p>
              )}
              {loadingParticipants ? (
                <p className='incidence-action-empty'>Cargando participantes de los grupos...</p>
              ) : form.groupIds.length === 0 ? (
                <p className='incidence-action-empty'>Selecciona al menos un grupo para cargar sus participantes.</p>
              ) : participants.length === 0 ? (
                <p className='incidence-action-empty'>Los grupos seleccionados no tienen participantes activos.</p>
              ) : (
                <div className='incidence-participant-list'>
                  {participants.map(participant => (
                    <article key={participant.id} className='incidence-participant'>
                      <div>
                        <strong>{participant.fullName}</strong>
                        <span>{participant.documentType} {participant.document}</span>
                      </div>
                      <button
                        type='button'
                        className={`incidence-signature-button${
                          participant.hasSignature ? ' incidence-signature-button--signed' : ''
                        }`}
                        disabled={!isEditing || groupsDirty || attendanceClosed || savingSignature}
                        onClick={() => openSignature(participant)}>
                        <span className='material-symbols-rounded' aria-hidden='true'>
                          {participant.hasSignature ? 'check_circle' : 'draw'}
                        </span>
                        {participant.hasSignature ? 'Firma registrada' : 'Firmar asistencia'}
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className='incidence-action-section'>
              <h2>Evidencias</h2>
              <div className='incidence-action-grid incidence-action-grid--three'>
                <label className='incidence-action-file'>
                  <span>Bitácora{attendanceClosed ? ' *' : ''}</span>
                  {existingEvidences.some(item => item.name === 'bitacora') && (
                    <small>Archivo cargado. Selecciona otro para reemplazarlo.</small>
                  )}
                  <input
                    type='file'
                    required={attendanceClosed && !existingEvidences.some(item => item.name === 'bitacora')}
                    accept='.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp'
                    onChange={event => updateForm('logbook', event.target.files?.[0] || null)}
                  />
                </label>
                <label className='incidence-action-file'>
                  <span>Ficha metodológica{attendanceClosed ? ' *' : ''}</span>
                  {existingEvidences.some(item => item.name === 'ficha_metodologica') && (
                    <small>Archivo cargado. Selecciona otro para reemplazarlo.</small>
                  )}
                  <input
                    type='file'
                    required={attendanceClosed && !existingEvidences.some(item => item.name === 'ficha_metodologica')}
                    accept='.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp'
                    onChange={event => updateForm('methodology', event.target.files?.[0] || null)}
                  />
                </label>
                <label className='incidence-action-file'>
                  <span>Asistencia</span>
                  {existingEvidences.some(item => item.name === 'asistencia') && (
                    <small>Archivo cargado. Selecciona otro para reemplazarlo.</small>
                  )}
                  <input
                    type='file'
                    accept='.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp'
                    onChange={event => updateForm('attendance', event.target.files?.[0] || null)}
                  />
                </label>
              </div>
            </section>

            <div className='incidence-action-actions'>
              {isEditing && (
                existingEvidences.some(evidence => evidence.name === 'asistencia') ||
                participants.some(participant => participant.hasSignature)
              ) && (
                <button
                  type='button'
                  className='incidence-action-actions__download'
                  disabled={downloadingAttendance}
                  onClick={downloadAttendance}>
                  <span className='material-symbols-rounded' aria-hidden='true'>download</span>
                  {downloadingAttendance ? 'Generando...' : 'Descargar asistencia'}
                </button>
              )}
              <button type='submit' disabled={saving || loadingParticipants}>
                {saving
                  ? 'Guardando...'
                  : isEditing
                    ? 'Guardar cambios'
                    : 'Guardar acción de incidencia'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

export default IncidenceActionForm;
